import { Crown, LayoutGrid, Store, TrendingUp, type LucideIcon } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type Profile = {
  Icon: LucideIcon;
  plan: string;
  title: string;
  desc: string;
  accent: 'lime' | 'terracotta' | 'charcoal';
};

const PROFILES: ReadonlyArray<Profile> = [
  {
    Icon: Store,
    plan: '→ Plan Starter',
    title: 'Kiosque, boutique de quartier',
    desc: 'Un seul point de vente, 1 à 2 caissiers. Tu veux remplacer Excel + la calculatrice par un vrai outil sans exploser ton budget.',
    accent: 'lime',
  },
  {
    Icon: TrendingUp,
    plan: '→ Plan Croissance',
    title: 'Commerce sérieux qui veut piloter',
    desc: 'Deux boutiques, Mobile Money, retours, rapports et premiers crédits IA. C’est le point d’entrée recommandé pour gérer proprement.',
    accent: 'terracotta',
  },
  {
    Icon: LayoutGrid,
    plan: '→ Plan Pro',
    title: 'Supérette, mini-marché multi-sites',
    desc: 'De 2 à 5 boutiques, transferts de stock entre points, tableau de bord consolidé, plusieurs caisses par magasin.',
    accent: 'charcoal',
  },
  {
    Icon: Crown,
    plan: '→ Plan Business',
    title: 'Chaîne, franchise, réseau national',
    desc: "10+ boutiques, ERP à intégrer, besoin d'un account manager, formation sur site et SLA contractuel.",
    accent: 'charcoal',
  },
];

const ACCENT: Record<Profile['accent'], string> = {
  lime: 'bg-lime-500 text-charcoal-800',
  terracotta: 'bg-terracotta-500 text-white',
  charcoal: 'bg-charcoal-800 text-lime-400',
};

export default function PricingProfilesV2() {
  return (
    <section className="bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel className="justify-center">Tu hésites ?</SectionLabel>
          <h2 className="mt-6 font-marketing-display text-[clamp(32px,4.2vw,54px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
            Quel plan pour <span className="underline-lime">ton commerce</span> ?
          </h2>
          <p className="mt-5 font-marketing-body text-lg text-charcoal-600">
            Le bon plan, c'est celui qui colle à ta réalité, pas à ta taille d'entreprise. Voici 4
            profils types.
          </p>
        </div>

        <div className="mt-14 grid gap-7 md:grid-cols-2 xl:grid-cols-4">
          {PROFILES.map((p, idx) => (
            <article
              key={p.title}
              className={`flex flex-col border-2 border-charcoal-800 bg-white p-7 shadow-neobrutal-6 transition-transform hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-10 ${idx === 1 ? 'xl:-translate-y-4' : ''}`}
            >
              <div
                className={`flex h-14 w-14 items-center justify-center border-2 border-charcoal-800 shadow-neobrutal-2 ${ACCENT[p.accent]}`}
              >
                <p.Icon className="h-6 w-6" strokeWidth={2.2} />
              </div>
              <div className="mt-5 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.14em] text-lime-700">
                {p.plan}
              </div>
              <h3 className="mt-2 font-marketing-display text-[22px] font-bold leading-tight tracking-[-0.02em] text-charcoal-800">
                {p.title}
              </h3>
              <p className="mt-3 font-marketing-body text-[15px] leading-relaxed text-charcoal-600">
                {p.desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
